/* eslint-disable import/named */
import React, {
  useImperativeHandle,
  forwardRef,
  useState,
  useRef,
  useEffect
} from "react";
import PropTypes from "kf-proptypes/widgets";
import { defineMessage } from "@lingui/macro";

import { i18n } from "base/i18n";
import { isEqual } from "base/utils";

import { RichTextEditor, FORMAT_TYPES } from "../index.js";
import { Icon } from "@widgets/common/components/primitives/icon/index.jsx";
import { Chip } from "@widgets/datadisplay/chip/index.jsx";
import {
  BaseDropdown,
  Menu,
  MenuItem,
  BASE_DROPDOWN_POSITION,
  BASE_DROPDOWN_TRIGGER
} from "../../dropdown/index.jsx";
import { SearchInput } from "../../search/index.jsx";

import styles from "./dynamicinput.css";

import PlusWithCircleIcon from "./PlusWithCircle.svg";

DynamicInput.propTypes = {
  /** Input value could be of 2 types, object and an array based on the isTitle prop */
  value: PropTypes.arrayOf(
    PropTypes.shape({
      Type: PropTypes.string.isRequired,
      Value: PropTypes.string.isRequired,
      IsSystemField: PropTypes.bool.isRequired,
      Kind: PropTypes.string.isRequired,
      Id: PropTypes.string.isRequired
    })
  ).isRequired,
  /** Empty placeholder for an editor */
  placeholder: PropTypes.string,
  /** List of fields to be shown when click of the button */
  fieldList: PropTypes.arrayOf(
    PropTypes.shape({
      Id: PropTypes.string.isRequired,
      Name: PropTypes.string.isRequired,
      Type: PropTypes.string.isRequired,
      IsSystemField: PropTypes.bool.isRequired,
      Model: PropTypes.string.isRequired,
      Required: PropTypes.bool.isRequired,
      IsInternal: PropTypes.bool.isRequired,
      SourceFlowId: PropTypes.string.isRequired,
      SourceFlowType: PropTypes.string.isRequired,
      IsComputedField: PropTypes.bool.isRequired
    })
  ),
  /** Field will indicates the input is an expression or not, will be true for lookups and false for the flow settings(Process, case) */
  isTitle: PropTypes.bool,
  /** An callback will be fired during onBlur */
  onUpdate: PropTypes.func,
  /** An callback will be fired during a change */
  onChange: PropTypes.func
};

DynamicInput.defaultProps = {
  value: "",
  placeholder: defineMessage`Type here`,
  fieldList: [],
  onUpdate: () => {},
  isTitle: false,
  onChange: () => {}
};

/** This component will render a plain editor and a button to the right to select the list of fields from the dropdown and on select it inserts on the editor */
export function DynamicInput(props) {
  const {
    value = "",
    placeholder,
    fieldList = [],
    onUpdate = () => {},
    isTitle = false,
    onChange = () => {}
  } = props;

  const fieldListRef = useRef([]);
  const valueRef = useRef("");
  const styleRef = useRef({});
  const [inputValue, setInputValue] = useState("");

  useEffect(
    function updateItems() {
      fieldListRef.current = fieldList;
    },
    [fieldList]
  );

  useEffect(
    function updateInputValue() {
      if (fieldList.length > 0) {
        if (!isEqual(valueRef.current, value)) {
          valueRef.current = value;
        }
        setInputValue(
          isTitle
            ? serializeFromTitle(valueRef.current)
            : serializeFromExpression(valueRef.current)
        );
      }
    },
    [fieldList.length, isTitle, value]
  );

  const customPlugins = [
    {
      formatType: FORMAT_TYPES.FIELD,
      component: forwardRef((prop, ref) => {
        useImperativeHandle(ref, () => {
          return {
            getData: () => {
              return prop.node;
            }
          };
        });
        return (
          <Chip onClose={() => prop.onDelete()} className={styles.actionchip}>
            {getFieldLabel(prop.node.field)}
          </Chip>
        );
      })
    }
  ];

  const editorRef = useRef();
  let fieldIdNameCache = {};

  function getFieldLabel(fieldId) {
    let label = fieldIdNameCache[fieldId];
    if (!label) {
      let field =
        fieldListRef.current.find((field) => {
          fieldIdNameCache[fieldId] = field.Label || field.Name;
          return field.Id === fieldId;
        }) || {};
      label = field.Label || field.Name;
    }
    return label;
  }

  function onBlurHandler(value) {
    let data = isTitle ? deSerializeToTitle(value) : deSerializeToString(value);
    onUpdate(data);
  }

  function serializeFromTitle(value) {
    let nodes = [];
    if (!value) return value;
    for (let node of value) {
      if (node.Type === "field") {
        let data = {
          field: node.Value,
          isSystemField: node.hasOwnProperty("IsSystemField")
            ? node.IsSystemField
            : true,
          id: node.Id
        };
        nodes.push({
          type: "field",
          data,
          nodes: [{ type: "text", text: "" }]
        });
      } else {
        nodes.push({ type: "text", text: node.Value || "" });
      }
    }
    return [{ type: "paragraph", data: {}, nodes }];
  }

  function serializeFromExpression(value) {
    let nodes = [];
    if (!value) return "";
    if (value) {
      let concatNode = value.getList("Expression", "Node")[0];
      let childNodes = concatNode.getList("Node", "Node");
      nodes = childNodes.map((node) => {
        if (node.getValue("Type") === "Field") {
          return {
            type: "field",
            data: {
              field: node.getValue("Field")
            },
            nodes: [{ type: "text", text: "" }]
          };
        }
        return {
          type: "text",
          text: node.getValue("Value") || ""
        };
      });
    } else {
      nodes.push({ type: "text", text: "" });
    }
    return [
      {
        type: "paragraph",
        data: {},
        nodes
      }
    ];
  }

  function deSerializeToTitle(value) {
    let node = value[0];
    let titleFragments = [];
    for (let textNode of node.nodes) {
      if (textNode.type === "text" && textNode.text.length > 0) {
        titleFragments.push({
          Type: "text",
          Value: textNode.text
        });
      }
      if (textNode.type === FORMAT_TYPES.FIELD) {
        titleFragments.push({
          Type: "field",
          Kind: "Field",
          Id: textNode.data.id || textNode.data.field,
          Value: textNode.data.field,
          IsSystemField: textNode.data.isSystemField
        });
      }
    }
    return titleFragments;
  }

  function deSerializeToString(value) {
    let node = value[0];
    let string = [];
    for (let textNode of node.nodes) {
      if (textNode.type === "text" && textNode.text.length > 0) {
        string.push(`"${textNode.text.trim()}"`);
      }
      if (textNode.type === "field") {
        string.push(textNode.data.field);
      }
    }
    return `concatenate(${string.join(",")})`;
  }

  function addField(field) {
    editorRef.current.insertBlock(FORMAT_TYPES.FIELD, {
      field: field.Id,
      isSystemField: field.IsSystemField
    });
  }

  function onChangeHandler(value) {
    let data = isTitle ? deSerializeToTitle(value) : deSerializeToString(value);
    onChange(data);
  }

  return (
    <div
      className={styles.dynamicLabel}
      id={"dynamicinput"}
      data-component="dynamicinput"
      ref={styleRef}
    >
      <RichTextEditor
        ref={editorRef}
        value={inputValue}
        className={styles.editor}
        placeholder={i18n._(placeholder)}
        onBlur={onBlurHandler}
        clearOnEnter={false}
        inlineToolbar={false}
        allowSlashCommand={false}
        shiftReturn={true}
        onEnter={() => {}}
        focus={false}
        plugins={customPlugins}
        onChange={onChangeHandler}
      />
      <FieldList
        onAddField={addField}
        fieldList={fieldList}
        styleRef={styleRef}
      />
    </div>
  );
}

FieldList.propTypes = {
  fieldList: PropTypes.arrayOf(PropTypes.object),
  onAddField: PropTypes.func,
  styleRef: PropTypes.objectOf(PropTypes.object)
};

function FieldList(props) {
  const { fieldList, onAddField, styleRef } = props;
  const [width, setWidth] = useState();
  const [isOpen, setIsOpen] = useState(false);
  const [currentFieldList, setCurrentFieldList] = useState([fieldList]);

  function onChange(e) {
    const value = e.target.value && e.target.value.trim();
    setCurrentFieldList((state) => {
      const newState = fieldList.filter(
        (item) => item.Name.toLowerCase().indexOf(value.toLowerCase()) > -1
      );
      return newState;
    });
  }

  useEffect(
    function updateFieldList() {
      setCurrentFieldList(fieldList);
    },
    [fieldList]
  );

  useEffect(
    function updateWidth() {
      setWidth(styleRef.current?.clientWidth);
    },
    [styleRef]
  );

  return (
    <BaseDropdown
      isOpen={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      position={BASE_DROPDOWN_POSITION.BOTTOM_RIGHT}
      trigger={[BASE_DROPDOWN_TRIGGER.CLICK]}
      getContainer={(element) => element.parentNode}
      targetRenderer={() => {
        return (
          <div className={`${styles.addIcon} dynamicinput`} tabIndex="-1">
            <Icon paddingNone={true}>
              <PlusWithCircleIcon fill={"var(--kf-color-gray-700"} />
            </Icon>
          </div>
        );
      }}
      listRenderer={() => {
        return (
          <div style={{ width }} className={styles.fieldList}>
            <SearchInput onChange={onChange} />
            <div className={styles.scrollbar}>
              <Menu>
                {currentFieldList.map((item, index) => {
                  return (
                    <MenuItem
                      onClick={() => {
                        setIsOpen(false);
                        onAddField(item);
                      }}
                      key={index}
                    >
                      {i18n._(item.Name)}
                    </MenuItem>
                  );
                })}
              </Menu>
            </div>
          </div>
        );
      }}
    />
  );
}
